// Button Component
// Professional button component using design system tokens

import React from 'react';
import { useTheme } from '../../design-system/ThemeProvider';

interface ButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  style?: React.CSSProperties;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  onClick,
  type = 'button',
  className = '',
  style = {}
}) => {
  const { theme } = useTheme();

  const getVariantStyles = () => {
    switch (variant) {
      case 'secondary':
        return theme.componentTokens.button.secondary;
      case 'danger':
        return theme.componentTokens.button.danger;
      default:
        return theme.componentTokens.button.primary;
    }
  };

  const getSizeStyles = () => {
    return theme.componentSizes.button[size];
  };

  const variantStyles = getVariantStyles();
  const sizeStyles = getSizeStyles();

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled && !loading) {
      e.currentTarget.style.backgroundColor = variantStyles.backgroundHover;
      if (variantStyles.shadowHover) {
        e.currentTarget.style.boxShadow = variantStyles.shadowHover;
      }
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled && !loading) {
      e.currentTarget.style.backgroundColor = variantStyles.background;
      e.currentTarget.style.boxShadow = variantStyles.shadow;
    }
  };

  const buttonStyles: React.CSSProperties = {
    ...variantStyles,
    ...sizeStyles,
    backgroundColor: disabled ? variantStyles.backgroundDisabled : variantStyles.background,
    color: disabled ? variantStyles.textDisabled : variantStyles.text,
    border: `1px solid ${variantStyles.border}`,
    borderRadius: variantStyles.borderRadius,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing[2],
    fontWeight: theme.typography.fontWeight.medium,
    transition: variantStyles.transition,
    boxShadow: variantStyles.shadow,
    ...style
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={className}
      style={buttonStyles}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {loading && (
        <div
          style={{
            width: '16px',
            height: '16px',
            border: `2px solid ${theme.semanticColors.border.primary}`,
            borderTop: `2px solid ${variantStyles.text}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}
        />
      )}
      {children}
    </button>
  );
};